import {useReducer} from "react";
import reducer from "./reducer";
import CampaignContext, {internalState} from "./context";
import {execute} from "../../utils/helpers/execute";
import EndPoints from "../../services/end-points";
import {ICampaign} from "../../models/campaign/response";
import {ICreateCampaign, IUpdateCampaign} from "../../models/campaign/request";
import {ICampaignQuery} from "../../models/campaign/query";

export interface IProps {
    children: React.ReactNode;
}

const CampaignContextProvider: React.FC<IProps> = (props) => {
    const [state, dispatch] = useReducer(reducer, internalState)

    const getData = async () => {
        await execute({
            callback: async () => {
                dispatch({ type: "LOADING", payload: { loading: "list" } });
                const { data } = await EndPoints.campaign.getAllCampaigns(state.query);
                dispatch({ type: "SET_LIST", payload: { list: data } });
            },
            fallback: (error) => {},
            finallyCallback: () => {
                dispatch({ type: "LOADING", payload: { loading: "list" } });
            },
            throwException: false,
        });
    };

    const getDetails = async (id: number) => {
        await execute({
            callback: async () => {
                dispatch({ type: "LOADING", payload: { loading: "details" } });
                const { data } = await EndPoints.campaign.getCampaign(id);
                dispatch({ type: "SET_DETAILS", payload: { details: data } });
            },
            fallback: (error) => {},
            finallyCallback: () => {
                dispatch({ type: "LOADING", payload: { loading: "details" } });
            },
            throwException: false,
        });
    };

    const setDetails = async (data?: ICampaign) => {
        dispatch({ type: "SET_DETAILS", payload: { details: data } });
    };

    const createCampaign = async (request: ICreateCampaign) => {
        await execute({
            callback: async () => {
                dispatch({ type: "LOADING", payload: { loading: "create" } });
                await EndPoints.campaign.createCampaign(request);
                getData();
            },
            fallback: (error) => {},
            finallyCallback: () => {
                dispatch({ type: "LOADING", payload: { loading: "create" } });
            },
            throwException: true,
        });
    };

    const updateCampaign = async (id: number, request: IUpdateCampaign) => {
        await execute({
            callback: async () => {
                dispatch({ type: "LOADING", payload: { loading: "update" } });
                await EndPoints.campaign.updateCampaign(id, request);
                getData();
            },
            fallback: (error) => {},
            finallyCallback: () => {
                dispatch({ type: "LOADING", payload: { loading: "update" } });
            },
            throwException: true,
        });
    };

    const deleteCampaign = async (id: number) => {
        await execute({
            callback: async () => {
                dispatch({ type: "LOADING", payload: { loading: "delete" } });
                await EndPoints.campaign.deleteCampaign(id);
                getData();
            },
            fallback: (error) => {},
            finallyCallback: () => {
                dispatch({ type: "LOADING", payload: { loading: "delete" } });
            },
            throwException: true,
        });
    };

    const setSearch = (search?: string) => {
        dispatch({ type: "SET_SEARCH", payload: { search } });
    };

    const setQuery = (query: ICampaignQuery) => {
        dispatch({ type: "SET_QUERY", payload: { query } });
    };

    return (
        <CampaignContext.Provider
            value={{
                ...state,
                actions: {
                    getData,
                    getDetails,
                    setDetails,
                    createCampaign,
                    updateCampaign,
                    deleteCampaign,
                    setSearch,
                    setQuery,
                }
            }}
        >
            {props.children}
        </CampaignContext.Provider>
    )
}

export default CampaignContextProvider;